<template>
    <pendo-popover-v2
        ref="popoverRef"
        data-cy="jump-to-popover"
        :title="$t('jumpTo.Jump to')"
        placement="right-start"
        :distance="popoverOffset"
        :popper-options="popperOptions"
        :hover="false"
        :container="false"
        :delay="{ show: 0, hide: 0 }"
        :popper-class="['jump-to-popover', { 'is-empty': isEmpty }]"
        @show="onPopoverShow"
        @hide="onPopoverClose">
        <template #default="{ shown }">
            <jump-to-nav-item
                ref="jumpToTrigger"
                data-cy="jump-to-trigger"
                :expanded="expanded"
                :is-open="shown"
                pendo-analytics="jump-to-nav-item"
                @mouseenter="$emit('mouseenter', 'jump-to')"
                @mouseleave="$emit('mouseleave')" />
        </template>
        <template #popover>
            <div
                ref="popoverContentWrapper"
                data-cy="jump-to-popover-content-wrapper"
                class="jump-to__popover-content-wrapper"
                @mouseenter="$emit('mouseenter', 'jump-to')"
                @mouseleave="$emit('mouseleave')"
                @keydown.enter="handleElementClick"
                @keydown.space="handleElementClick"
                @keydown.up="handleArrowKeydown"
                @keydown.down="handleArrowKeydown">
                <pendo-input
                    ref="jumpToInput"
                    class="pendo-search pendo-search--full-width jump-to__search"
                    :value="query"
                    role="combobox"
                    aria-autocomplete="list"
                    :aria-controls="isEmpty ? undefined : 'go-to-page-list'"
                    aria-haspopup="listbox"
                    :aria-expanded="String(!isEmpty)"
                    :aria-activedescendant="isEmpty ? undefined : `go-to-page-option-${activeItem}`"
                    :placeholder="$t('jumpTo.Go to any page in Pendo')"
                    @keydown.space.stop
                    @input="query = $event">
                    <template #prefix>
                        <pendo-icon
                            type="search"
                            stroke="var(--text-body-default-base)"
                            size="16" />
                    </template>
                    <template #suffix>
                        <pendo-icon-button
                            v-if="query"
                            class="pendo-search__clear"
                            icon="x"
                            :aria-label="$t('searchInput.Clear search text')"
                            @mousedown.prevent
                            @keyup.enter="onSearchClear"
                            @click="onSearchClear" />
                    </template>
                </pendo-input>
                <div
                    v-if="isEmpty"
                    data-cy="jump-to-empty-state"
                    class="jump-to-empty-state">
                    <div class="jump-to-empty-state__title">
                        {{ $t('jumpTo.No pages found') }}
                    </div>
                    <div class="jump-to-empty-state__subtitle">
                        {{ $t("jumpTo.We couldn't find anything by that name.") }}
                    </div>
                </div>
                <div
                    v-else
                    ref="jumpToPopoverContent"
                    class="jump-to__popover-content">
                    <ul
                        id="go-to-page-list"
                        data-cy="jump-to-list"
                        role="listbox"
                        class="jump-to-list">
                        <li
                            v-for="(route, index) in routesFilteredBySearch"
                            :id="`go-to-page-option-${index}`"
                            :key="route.id"
                            v-pendo-tooltip="{
                                content: tooltipContent,
                                placement: 'right',
                                arrow: true,
                                delay: { show: 1000, hide: 0 },
                            }"
                            role="option"
                            data-cy="jump-to-list-item"
                            class="jump-to-list__item"
                            :class="{ 'jump-to-list__item--active': activeItem === index }"
                            :aria-selected="activeItem === index"
                            @mouseenter="activeItem = index"
                            @focus="activeItem = index">
                            <pendo-router-link
                                :ref="`jump-to-list-item-${index}`"
                                class="jump-to-list__link"
                                :to="route"
                                role="presentation"
                                tabindex="-1"
                                @mouseover="onMouseover(route)"
                                @click="handleElementClick">
                                <pendo-icon
                                    class="jump-to-list__prefix-icon"
                                    :type="route.icon.type"
                                    size="16" />
                                <div
                                    :ref="`jump-to_${route.id}`"
                                    class="jump-to-list__label">
                                    {{ route.label }}
                                </div>
                                <pendo-icon
                                    v-if="activeItem === index"
                                    class="jump-to-list__suffix-icon"
                                    type="arrow-up-right"
                                    size="16"
                                    stroke="var(--text-body-subtle-base)" />
                            </pendo-router-link>
                        </li>
                    </ul>
                </div>
            </div>
        </template>
    </pendo-popover-v2>
</template>

<script>
import { computed, ref } from 'vue';
import { useWindowSize } from '@vueuse/core';
import { useArrowKeyNavigation } from '@/common/use/use-arrow-key-navigation/use-arrow-key-navigation';
import { PendoPopoverV2, PendoIcon, PendoIconButton, PendoInput, PendoTooltip, useFocusTrap } from '@pendo/components';
import JumpToNavItem from '@/side-nav/components/top-level-nav-items/jump-to-nav-item.vue';
import PendoRouterLink from '@/vue-router/pendo-router-link.vue';
import { flattenRoutes } from '@/vue-router/route.utils';
import { getFirstFocusableNode, elementHasOverflowX } from '@/utils/dom.utils.js';
import { v4 as uuid } from 'uuid';
import { eventIsOpeningANewTabOrWindow } from '@/utils/window.utils';
import useAriaLiveRegistry from '@/common/use/use-aria-live-registry/use-aria-live-registry';

export default {
    components: {
        PendoPopoverV2,
        PendoIcon,
        PendoIconButton,
        PendoInput,
        PendoRouterLink,
        JumpToNavItem,
    },
    directives: {
        PendoTooltip,
    },
    props: {
        openPopover: {
            type: String,
            default: '',
        },
        expanded: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['handlePopover', 'mouseenter', 'mouseleave'],
    setup (props) {
        const jumpToPopoverContent = ref(null);
        const popoverContentWrapper = ref(null);
        const { width: windowWidth } = useWindowSize();
        const { handleArrowKeyNavigation } = useArrowKeyNavigation({
            containerRef: jumpToPopoverContent,
            selector: '.jump-to-list__item',
        });

        const isPopoverOpen = computed(() => props.openPopover === 'jump-to');
        const isSmallScreen = computed(() => windowWidth.value < 576);

        const popperOptions = computed(() => {
            if (isSmallScreen.value) {
                return {
                    modifiers: {
                        flip: { enabled: false },
                        preventOverflow: { enabled: false },
                    },
                };
            }

            return {};
        });

        const popoverOffset = computed(() => {
            return isSmallScreen.value ? '0' : '8';
        });

        useFocusTrap(popoverContentWrapper, isPopoverOpen, {
            initialFocus: false,
            escapeDeactivates: false,
            clickOutsideDeactivates: false,
        });

        const { queueMessage } = useAriaLiveRegistry();

        return {
            popperOptions,
            popoverOffset,
            jumpToPopoverContent,
            popoverContentWrapper,
            handleArrowKeyNavigation,
            queueMessage,
        };
    },
    data () {
        return {
            query: '',
            tooltipContent: null,
            activeItem: 0,
            isComposing: false,
        };
    },
    computed: {
        routesFilteredByPermissionsAndNavLinks () {
            return flattenRoutes(this.$router.options.routes)
                .reduce((acc, route) => {
                    let hasPermissionsForRoute = true;

                    if (route.meta?.hasPermissionsDeniedRedirect) {
                        // returns either a route object or false
                        hasPermissionsForRoute = !route.meta.hasPermissionsDeniedRedirect();
                    }

                    if (hasPermissionsForRoute) {
                        route.meta?.navigationQuickLinks?.forEach((navLink) => {
                            const { hasPermissionsForSubRoute, label, ...navLinkDetails } = navLink;
                            const hasSubRoutePermissions = hasPermissionsForSubRoute?.() ?? true;

                            if (typeof label !== 'function') {
                                throw new Error(
                                    `JumpTo: Navigation quick link label must be a function for route ${route.name}`,
                                );
                            }

                            if (hasSubRoutePermissions) {
                                acc.push({ ...navLinkDetails, label: label(this.$t), name: route.name, id: uuid() });
                            }
                        });
                    }

                    return acc;
                }, [])
                .sort((a, b) => a.label.localeCompare(b.label));
        },
        routesFilteredBySearch () {
            if (!this.query) {
                return this.routesFilteredByPermissionsAndNavLinks;
            }

            return this.routesFilteredByPermissionsAndNavLinks.filter(({ label, keywords }) => {
                if (keywords?.length) {
                    return (
                        label.toLowerCase().includes(this.query.toLowerCase()) ||
                        keywords.some((keyword) => keyword.toLowerCase().includes(this.query.toLowerCase()))
                    );
                }

                return label.toLowerCase().includes(this.query.toLowerCase());
            });
        },
        isEmpty () {
            return this.routesFilteredBySearch.length === 0;
        },
        showJumpToPopover () {
            return this.openPopover === 'jump-to';
        },
        isMac () {
            return navigator.userAgent.toLowerCase().includes('mac');
        },
    },
    watch: {
        query () {
            this.activeItem = 0;
        },
        isEmpty (newVal) {
            if (newVal && this.query) {
                this.queueMessage({
                    message: this.$t('jumpTo.No pages found'),
                    ariaLiveMode: 'polite',
                });
            }
        },
        showJumpToPopover: {
            async handler (showPopover) {
                await this.$nextTick();
                const ref = this.$refs.popoverRef;
                if (showPopover) {
                    if (!ref?.isShown) {
                        ref?.show?.();
                    }
                } else if (ref?.isShown) {
                    ref.hide?.();
                }
            },
            immediate: true,
        },
    },
    mounted () {
        window.addEventListener('keydown', this.onKeypress);
        window.addEventListener('compositionstart', this.compositionStart);
        window.addEventListener('compositionend', this.compositionEnd);
    },
    unmounted () {
        window.removeEventListener('keydown', this.onKeypress);
        window.removeEventListener('compositionstart', this.compositionStart);
        window.removeEventListener('compositionend', this.compositionEnd);
    },
    methods: {
        onSearchClear () {
            this.query = '';
            this.$nextTick(() => {
                getFirstFocusableNode(this.$refs.jumpToInput?.$el)?.focus();
            });
        },
        onKeypress ($event) {
            const isShortcut = this.isMac
                ? $event.metaKey && $event.code === 'KeyK'
                : $event.ctrlKey && $event.altKey && $event.code === 'KeyK';

            if (isShortcut) {
                this.$refs.jumpToTrigger?.$el?.focus();
                this.$refs.popoverRef?.show?.();
            }
        },
        onPopoverShow () {
            this.$emit('handlePopover', 'jump-to');
            this.query = '';
            this.activeItem = 0;
            this.$refs.jumpToTrigger?.$el?.focus();
            // wait for the popover to open so the children are mounted
            setTimeout(() => {
                getFirstFocusableNode(this.$refs.jumpToInput?.$el)?.focus();
                this.$refs.jumpToPopoverContent?.scrollTo({ top: 0, behavior: 'instant' });
            }, 200);
        },
        onPopoverClose () {
            if (this.openPopover === 'jump-to' || !this.openPopover) {
                this.$emit('handlePopover');
            }
        },
        onMouseover (route) {
            const labelRefName = `jump-to_${route.id}`;
            const { clientWidth, scrollWidth } = this.$refs[labelRefName]?.[0] || {};
            const hasOverflow = elementHasOverflowX({ clientWidth, scrollWidth });

            if (hasOverflow) {
                this.tooltipContent = route.label;
            } else {
                this.tooltipContent = null;
            }
        },
        handleElementClick ($event) {
            if (this.isComposing || eventIsOpeningANewTabOrWindow($event)) return;

            if ($event.type === 'keydown') {
                if ($event.target.closest('.pendo-search__clear')) return;

                this.$refs[`jump-to-list-item-${this.activeItem}`]?.[0]?.$el?.click();
            }

            this.$refs.popoverRef?.hide?.();
        },
        compositionStart () {
            this.isComposing = true;
        },
        compositionEnd () {
            this.isComposing = false;
        },
        handleArrowKeydown ($event) {
            const nextIndex = this.handleArrowKeyNavigation($event, this.activeItem);

            if (nextIndex !== undefined) {
                this.activeItem = nextIndex;
            }

            this.$refs[`jump-to-list-item-${this.activeItem}`]?.[0]?.$el?.scrollIntoView({
                behavior: 'instant',
                block: 'nearest',
            });
        },
    },
};
</script>

<style scoped lang="less">
@import '@/side-nav/nav.style.less';

.jump-to__popover-content-wrapper {
    height: 343px;
    display: flex;
    flex-direction: column;
    height: 100%;
    width: 100%;
    border-radius: 4px;

    &:focus-visible {
        outline: 2px solid var(--palette-gray-90);
        outline-offset: -1px;
    }
}

.jump-to__popover-content {
    overflow-y: auto;
    overflow-x: hidden;
    min-height: 0;
}

.jump-to__search {
    padding: 12px 14px;

    @media (min-resolution: 2dppx) {
        padding: 8px 10px;
    }
}

:deep(.pendo-search__clear) {
    color: var(--text-body-subtle-base);

    &:hover {
        color: var(--text-body-default-base);
    }
}

.jump-to-empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    justify-self: center;
    gap: 6px;
    width: 100%;
    height: 100%;
    flex-grow: 1;

    &__title {
        font-weight: 600;
        font-size: 14px;
        color: var(--text-body-default-base);
    }

    &__subtitle {
        font-size: 12px;
        font-weight: 400;
        text-align: center;
        color: var(--text-body-subtle-base);
    }
}

.jump-to-list {
    list-style-type: none;
    padding: 0px;

    &__item {
        display: flex;
        align-items: center;

        &--active {
            background-color: var(--surface-default-hover);
        }
    }

    &__link {
        height: 29px;
        cursor: pointer;
        display: flex;
        gap: 8px;
        text-decoration: none;
        color: var(--text-body-default-base);
        align-items: center;
        padding-left: 8px;
        padding-right: 10px;
        width: 100%;

        &:focus-visible {
            outline: 2px solid var(--palette-gray-90);
            outline-offset: -2px;
        }
    }

    &__prefix-icon {
        padding-left: 8px;
    }

    &__label {
        font-family: 'Inter';
        font-size: 14.22px;
        font-weight: 400;
        width: 238px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
}
</style>

<style lang="less">
.jump-to-popover {
    .pendo-popover-v2__panel {
        width: clamp(220px, 55vh, 319px);
        height: clamp(180px, 55vh, 343px);
        padding: 0;
        border-radius: 4px;
        display: flex;
    }

    &.is-empty .pendo-popover-v2__panel {
        justify-content: center;
        align-items: center;
    }
}
</style>
